import React from "react";
import { Badge, type BadgeProps, Box, Icon } from "@chakra-ui/react";
import type { IconType } from "react-icons";
import {
  FiAlertTriangle,
  FiCheck,
  FiCheckCircle,
  FiCircle,
  FiClock,
  FiEdit3,
  FiHelpCircle,
  FiLoader,
  FiMail,
  FiSend,
  FiXCircle,
} from "react-icons/fi";
import { type Tone, toneRgb, toneTokens } from "./tones";

export type StatusKind = "ticket" | "stock" | "application" | "generic";

export interface StatusMeta {
  tone: Tone;
  label: string;
  icon: IconType;
  /** dot pulses (reorder, in progress) */
  pulse: boolean;
  variant: `subtle-${Tone}`;
  /** false when the raw value was not recognised (label is then the title-cased raw text) */
  known: boolean;
}

export interface StatusBadgeProps extends Omit<BadgeProps, "variant"> {
  kind: StatusKind;
  /** Raw domain value, e.g. ticket.status.text — normalised with trim().toLowerCase(). */
  value: string;
  /** Show the Feather icon instead of the 6px dot. */
  showIcon?: boolean;
}

type Entry = { tone: Tone; label: string; icon: IconType; pulse?: boolean };

const TICKET: Record<string, Entry> = {
  "not started": { tone: "neutral", label: "Not started", icon: FiCircle },
  open: { tone: "info", label: "Open", icon: FiMail },
  "in progress": { tone: "warning", label: "In progress", icon: FiLoader, pulse: true },
  closed: { tone: "success", label: "Closed", icon: FiCheck },
};

const STOCK: Record<string, Entry> = {
  reorder: { tone: "danger", label: "Reorder now", icon: FiAlertTriangle, pulse: true },
  low: { tone: "warning", label: "Running low", icon: FiClock },
  ok: { tone: "success", label: "OK", icon: FiCheckCircle },
  unknown: { tone: "neutral", label: "Not enough data", icon: FiHelpCircle },
};

const APPLICATION: Record<string, Entry> = {
  incomplete: { tone: "warning", label: "Incomplete", icon: FiEdit3 },
  draft: { tone: "warning", label: "Incomplete", icon: FiEdit3 },
  submitted: { tone: "info", label: "Submitted", icon: FiSend },
  pending: { tone: "info", label: "Pending review", icon: FiClock },
  "in review": { tone: "info", label: "Pending review", icon: FiClock },
  approved: { tone: "success", label: "Approved", icon: FiCheckCircle },
  active: { tone: "success", label: "Approved", icon: FiCheckCircle },
  rejected: { tone: "danger", label: "Rejected", icon: FiXCircle },
  declined: { tone: "danger", label: "Rejected", icon: FiXCircle },
  overdue: { tone: "danger", label: "Overdue", icon: FiAlertTriangle },
  clear: { tone: "success", label: "Clear", icon: FiCheckCircle },
};

const GENERIC: Record<string, Entry> = {
  success: { tone: "success", label: "Success", icon: FiCheckCircle },
  warning: { tone: "warning", label: "Warning", icon: FiAlertTriangle },
  danger: { tone: "danger", label: "Danger", icon: FiXCircle },
  error: { tone: "danger", label: "Error", icon: FiXCircle },
  info: { tone: "info", label: "Info", icon: FiHelpCircle },
  neutral: { tone: "neutral", label: "Neutral", icon: FiCircle },
};

const TABLES: Record<StatusKind, Record<string, Entry>> = { ticket: TICKET, stock: STOCK, application: APPLICATION, generic: GENERIC };

const titleCase = (s: string) => s.replace(/[_-]+/g, " ").toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());

/** Resolve a raw status string to tone/label/icon. Unknown values fall back to neutral with the title-cased raw text. */
export const getStatusMeta = (kind: StatusKind, value: string): StatusMeta => {
  const key = (value ?? "").trim().toLowerCase();
  const entry = TABLES[kind][key];
  if (entry) {
    return { tone: entry.tone, label: entry.label, icon: entry.icon, pulse: entry.pulse ?? false, variant: `subtle-${entry.tone}`, known: true };
  }
  return { tone: "neutral", label: key ? titleCase(key) : "—", icon: FiCircle, pulse: false, variant: "subtle-neutral", known: false };
};

/**
 * Domain status → themed pill. Never colour-only: a dot or icon always accompanies the text.
 * @example <StatusBadge kind="ticket" value={ticket.status.text} />
 * @example <StatusBadge kind="stock" value={item.status} showIcon />
 */
export const StatusBadge: React.FC<StatusBadgeProps> = ({ kind, value, showIcon = false, ...rest }) => {
  const meta = getStatusMeta(kind, value);
  const tokens = toneTokens(meta.tone);
  return (
    <Badge variant={meta.variant} title={meta.known ? undefined : value} {...rest}>
      {showIcon ? (
        <Icon as={meta.icon} boxSize="12px" color={tokens.solid} aria-hidden />
      ) : (
        <Box
          as="span"
          aria-hidden
          w="6px"
          h="6px"
          borderRadius="full"
          bg={tokens.solid}
          flexShrink={0}
          className={meta.pulse ? "mbf-pulse" : undefined}
          style={{ "--mbf-pulse-rgb": toneRgb[meta.tone] } as React.CSSProperties}
        />
      )}
      {meta.label}
    </Badge>
  );
};
